import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// Function to truncate text with ellipsis
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

// Common styled components for axis elements
const AxisLabel = styled.text`
  font-size: 12px;
  font-weight: 500;
  fill: var(--text-secondary);
`;

const XAxisLabel = styled(AxisLabel)`
  text-anchor: middle;
`;

// Modified YAxisLabel to handle text truncation with left alignment
const YAxisLabel = styled(AxisLabel)`
  text-anchor: start;
  dominant-baseline: middle;
  cursor: default;
`;

const AxisTitle = styled.text`
  font-size: 14px;
  font-weight: 600;
  fill: var(--text-primary);
  text-anchor: middle;
`;

// FIXED: Main chart grid for the two-chart layout with improved responsive design
const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  min-height: 800px; /* Add minimum height to ensure visibility */

  @media (min-width: 1000px) {
    grid-template-columns: 1fr 1fr;
    min-height: 500px; /* Can be shorter in two-column layout */
  }
`;

// FIXED: Chart container with minimum height to ensure visibility
const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 80%;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
  margin-bottom: 1.5rem; /* Add space between stacked charts in mobile view */
  padding-bottom: 20px;
  &.variants-chart {
    padding-bottom: 10px;

    @media (max-width: 1000px) {
      border-top: 1px solid var(--border-color);
      padding-top: 1rem;
    }
  }
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
  padding: 1.25rem 1.5rem 0.5rem;
  text-align: left;
`;

const ChartSubtitle = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  padding: 0 1.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PharmacyLabel = styled.span`
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-align: right;
  padding: 0.25rem 1rem;
`;

const FilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #aec7ed;
  color: var(--chart-primary);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 2rem;
  gap: 0.5rem;

  .clear-filter {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const FixedHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: var(--card-bg);
  z-index: 15;
  padding: 2rem 0 0.75rem;
  display: flex;
  justify-content: start;
  align-items: start;
  width: 100%;
`;

const ChartPlaceholder = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 400px;
  background: var(--card-highlight-bg);
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  box-sizing: border-box;
  position: relative;

  /* Enhanced WebKit scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--card-bg);
    border-radius: 6px;
    margin: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--chart-primary-translucent);
    border-radius: 6px;
    border: 2px solid var(--card-bg);
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--chart-primary);
    cursor: pointer;
  }

  &::-webkit-scrollbar-corner {
    background: var(--card-bg);
  }

  /* Firefox scrollbar styles */
  scrollbar-width: thin;
  scrollbar-color: var(--chart-primary-translucent) var(--card-bg);

  /* Add smooth scrolling for better UX */
  scroll-behavior: smooth;
`;

const ScrollableChartContent = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: start;
  padding: 0 10px 20px 0;
  position: relative; /* Required for absolute tooltip positioning */
`;

const TooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.1s ease;

  &.visible {
    opacity: 1;
  }

  .tooltip-title {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }

  .tooltip-subtitle {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.25rem;
  }

  .tooltip-count {
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
  }

  .tooltip-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 0.5rem;
  }

  .tooltip-note {
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-top: 0.25rem;
    font-style: italic;
  }
`;

// New tooltip for displaying full medication name on hover
const NameTooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-md);
  pointer-events: none;
  z-index: 31;
  opacity: 0;
  transition: opacity 0.2s ease;
  max-width: 300px;
  color: var(--text-primary);
  font-size: 0.875rem;

  &.visible {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;

  .icon {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: var(--text-tertiary);
  }

  .message {
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--card-bg);
  opacity: 0.9;
  z-index: 25;
  border-radius: var(--border-radius);
`;

// Search and sort controls for medication chart
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 1.5rem 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SearchInput = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: 768px) {
    max-width: 300px;
  }
`;

const SearchField = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  padding-left: 2.25rem;
  padding-right: ${(props) => (props.value ? "2.25rem" : "0.75rem")};
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--chart-primary);
    box-shadow: 0 0 0 1px var(--chart-primary-translucent);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 50%;

  &:hover {
    background-color: var(--button-secondary-bg);
    color: var(--text-primary);
  }
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }

  &:focus {
    outline: none;
    border-color: var(--chart-primary);
    box-shadow: 0 0 0 1px var(--chart-primary-translucent);
  }
`;

// Hook for responsive chart dimensions
const useResponsiveChart = (containerRef) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(300, width), // Minimum width of 300px
          height
        });
      }
    };

    // Initial calculation
    updateDimensions();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Also listen for window resize events
    window.addEventListener("resize", updateDimensions);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      window.removeEventListener("resize", updateDimensions);
    };
  }, [containerRef]);

  return dimensions;
};

// Main chart for medications
const MedicationsChart = ({
  medicationData,
  onMedicationSelect,
  selectedMedication,
  isLoading = false,
  selectedPharmacy,
  pharmacyOptions,
  onPharmacyChange
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("ordersDesc");
  const [filteredData, setFilteredData] = useState([]);
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    name: "",
    orders: 0
  });

  // Add state for the name tooltip
  const [nameTooltipState, setNameTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    fullName: ""
  });

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const nameTooltipRef = useRef(null); // New ref for the name tooltip
  const scrollContainerRef = useRef(null);

  // Use the responsive hook to get current dimensions
  const dimensions = useResponsiveChart(containerRef);

  // Filter and sort data when inputs change
  useEffect(() => {
    if (!medicationData || !medicationData.length) {
      setFilteredData([]);
      return;
    }

    // Filter by search term
    let filtered = medicationData;
    if (searchTerm.trim()) {
      filtered = medicationData.filter((med) =>
        med.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort data
    let sorted = [...filtered];
    switch (sortOrder) {
      case "ordersDesc":
        sorted.sort((a, b) => b.orders - a.orders);
        break;
      case "ordersAsc":
        sorted.sort((a, b) => a.orders - b.orders);
        break;
      case "nameAsc":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "nameDesc":
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        // Default to descending order
        sorted.sort((a, b) => b.orders - a.orders);
    }

    setFilteredData(sorted);
  }, [medicationData, searchTerm, sortOrder]);

  // Handle name label hover for displaying the full name tooltip
  const handleNameHover = (event, fullName) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set name tooltip state
    setNameTooltipState({
      visible: true,
      x: mouseX - 10,
      y: mouseY,
      fullName: fullName
    });
  };

  const handleNameLeave = () => {
    setNameTooltipState((prev) => ({ ...prev, visible: false }));
  };

  if (!medicationData || medicationData.length === 0) {
    return (
      <ChartContainer ref={containerRef}>
        {/* <ChartTitle>Medications by Orders</ChartTitle> */}
        <ChartPlaceholder className="empty">
          <EmptyState>
            <div className="icon">💊</div>
            <div className="message">No medication data available</div>
          </EmptyState>
        </ChartPlaceholder>
      </ChartContainer>
    );
  }

  // Define fixed width for medication name labels and max char length
  const FIXED_LABEL_WIDTH = 120;
  const MAX_CHARS = 15; // Maximum characters before truncation

  // Setup chart dimensions and scales
  const margin = {
    top: 20,
    right: 60,
    bottom: 50,
    left: FIXED_LABEL_WIDTH + 20
  }; // Adjusted left margin for fixed width labels
  const chartWidth = dimensions.width || 600; // Fallback width if dimensions not ready
  const width = chartWidth - margin.left - margin.right;

  const barHeight = 30;
  const barPadding = 10;
  const barSpacing = barHeight + barPadding;

  const contentHeight = Math.max(
    350,
    filteredData.length * barSpacing + margin.top + margin.bottom
  );

  // Calculate scale for bar widths
  const maxOrders = Math.max(...filteredData.map((d) => d.orders), 1);
  const xScale = (value) => (value / maxOrders) * width;
  const yScale = (index) => margin.top + index * barSpacing;

  // Generate evenly spaced tick values
  const tickCount = 5;
  const xTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((maxOrders / tickCount) * i)
  );

  // Handle bar hover for tooltip
  const handleBarHover = (event, d) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set tooltip state with exact mouse position
    setTooltipState({
      visible: true,
      x: mouseX + 10, // Small offset to not cover cursor
      y: mouseY + 10, // Small offset to not cover cursor
      name: d.name,
      orders: d.orders
    });
  };

  const handleBarLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // Handle bar click to select a medication
  const handleBarClick = (medication) => {
    onMedicationSelect(medication);
  };

  // Function to determine if a medication is selected
  const isMedicationSelected = (medication) => {
    return selectedMedication && selectedMedication.name === medication.name;
  };

  // Handle search and sort changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  return (
    <ChartContainer ref={containerRef} className="medications-chart">
      <ChartTitle></ChartTitle>

      {/* Filter badge */}
      {selectedMedication && (
        <ChartSubtitle>
          <FilterBadge>
            Filtered by: {selectedMedication.name}
            <span
              className="clear-filter"
              onClick={() => onMedicationSelect(null)}
              title="Clear filter"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </span>
          </FilterBadge>
        </ChartSubtitle>
      )}

      {/* Search and sort controls */}
      <SearchContainer>
        <SearchInput>
          <SearchIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchIcon>
          <SearchField
            type="text"
            placeholder="Search medications..."
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label="Search medications"
          />
          {searchTerm && (
            <ClearSearchButton
              onClick={handleClearSearch}
              aria-label="Clear search"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </ClearSearchButton>
          )}
        </SearchInput>

        <SortSelect
          value={sortOrder}
          onChange={handleSortChange}
          aria-label="Sort medications"
        >
          <option value="ordersDesc">Orders: High to Low</option>
          <option value="ordersAsc">Orders: Low to High</option>
          <option value="nameAsc">Name: A to Z</option>
          <option value="nameDesc">Name: Z to A</option>
        </SortSelect>

        <PharmacySelect
          value={selectedPharmacy}
          onChange={onPharmacyChange}
          aria-label="Filter by pharmacy"
        >
          <option value="All">All Pharmacies</option>
          {pharmacyOptions.map((pharmacy) => (
            <option key={pharmacy} value={pharmacy}>
              {pharmacy}
            </option>
          ))}
        </PharmacySelect>
      </SearchContainer>

      <FixedHeader>
        <div style={{ width: "100%", position: "relative" }}>
          <svg width="100%" height="40" style={{ overflow: "visible" }}>
            <g transform={`translate(${margin.left},15)`}>
              {/* X-Axis title */}
              <AxisTitle x={width / 2} y="-30">
                Number of Orders
              </AxisTitle>

              {/* X-Axis labels without lines */}
              {xTicks.map((tick, i) => (
                <g key={i}>
                  <XAxisLabel x={xScale(tick)} y="20">
                    {tick}
                  </XAxisLabel>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </FixedHeader>

      <ChartPlaceholder ref={scrollContainerRef}>
        {isLoading && (
          <LoadingOverlay>
            <div className="loading-spinner" />
          </LoadingOverlay>
        )}

        {filteredData.length === 0 && !isLoading && (
          <EmptyState>
            <div className="icon">🔍</div>
            <div className="message">
              No medications found matching "{searchTerm}"
            </div>
          </EmptyState>
        )}

        {filteredData.length > 0 && (
          <ScrollableChartContent ref={chartRef}>
            <svg
              width="100%"
              height={contentHeight}
              viewBox={`0 0 ${chartWidth} ${contentHeight}`}
              preserveAspectRatio="xMinYMin meet"
            >
              <g transform={`translate(${margin.left},0)`}>
                {/* Horizontal grid lines */}
                {filteredData.map((_, i) => (
                  <line
                    key={`grid-${i}`}
                    x1="0"
                    y1={yScale(i) + barHeight / 2}
                    x2={width}
                    y2={yScale(i) + barHeight / 2}
                    stroke="var(--border-color)"
                    strokeOpacity="0.2"
                    strokeDasharray="4,4"
                  />
                ))}

                {/* Vertical grid lines */}
                {xTicks.map((tick, i) => (
                  <line
                    key={`vgrid-${i}`}
                    x1={xScale(tick)}
                    y1="0"
                    x2={xScale(tick)}
                    y2={contentHeight - margin.bottom}
                    stroke="var(--border-color)"
                    strokeOpacity="0.2"
                    strokeDasharray="4,4"
                  />
                ))}

                {/* Bars with mouse event handlers */}
                {filteredData.map((d, i) => {
                  const isSelected = isMedicationSelected(d);
                  const truncatedName = truncateText(d.name, MAX_CHARS);

                  return (
                    <g key={i}>
                      {/* Background for the name label to make the clickable area more obvious */}
                      <rect
                        x={-FIXED_LABEL_WIDTH - 10}
                        y={yScale(i)}
                        width={FIXED_LABEL_WIDTH}
                        height={barHeight}
                        fill="transparent"
                        onMouseOver={(e) => handleNameHover(e, d.name)}
                        onMouseLeave={handleNameLeave}
                      />

                      {/* Medication name (y-axis) with truncation and left alignment */}
                      <YAxisLabel
                        x={-FIXED_LABEL_WIDTH - 5}
                        y={yScale(i) + barHeight / 2}
                        fontWeight={isSelected ? "600" : "500"}
                        onMouseOver={(e) => handleNameHover(e, d.name)}
                        onMouseLeave={handleNameLeave}
                      >
                        {truncatedName.length > 10
                          ? truncatedName.slice(0, 10) + "..."
                          : truncatedName}
                      </YAxisLabel>

                      {/* Clickable bar area */}
                      <g
                        onClick={() => handleBarClick(d)}
                        onMouseMove={(e) => handleBarHover(e, d)}
                        onMouseLeave={handleBarLeave}
                        style={{ cursor: "pointer" }}
                      >
                        {/* Bar */}
                        <rect
                          x="0"
                          y={yScale(i)}
                          width={xScale(d.orders)}
                          height={barHeight}
                          // fill="var(--chart-primary)"
                          fill="#AEC7ED"
                          rx="4"
                          opacity={isSelected ? "1" : "0.75"}
                          stroke={
                            isSelected
                              ? "var(--chart-primary-translucent)"
                              : "none"
                          }
                          strokeWidth={isSelected ? "2" : "0"}
                          className="chart-bar"
                        />

                        {/* Order count - at the end of the bar */}
                        <text
                          x={xScale(d.orders) + 10}
                          y={yScale(i) + barHeight / 2 + 5}
                          fontSize="14"
                          fontWeight="600"
                          fill="var(--text-primary)"
                          pointerEvents="none"
                        >
                          {d.orders}
                        </text>
                      </g>
                    </g>
                  );
                })}
              </g>

              {/* Screen reader description */}
              <desc>
                Horizontal bar chart showing medications by number of orders.
                {filteredData
                  .map((d) => `${d.name} has ${d.orders} orders.`)
                  .join(" ")}
              </desc>
            </svg>

            {/* Tooltip for bar hover */}
            <TooltipContainer
              ref={tooltipRef}
              className={tooltipState.visible ? "visible" : ""}
              style={{
                position: "absolute",
                left: `${tooltipState.x}px`,
                top: `${tooltipState.y}px`
              }}
            >
              <div className="tooltip-title">{tooltipState.name}</div>
              <div className="tooltip-count">
                <div
                  className="tooltip-color"
                  style={{ backgroundColor: "var(--chart-primary)" }}
                />
                <span>{tooltipState.orders} Orders</span>
              </div>
              <div className="tooltip-subtitle">Click to view variants</div>
            </TooltipContainer>

            {/* Name tooltip for showing full medication name on hover */}
            <NameTooltipContainer
              ref={nameTooltipRef}
              className={nameTooltipState.visible ? "visible" : ""}
              style={{
                position: "absolute",
                left: `${nameTooltipState.x}px`,
                top: `${nameTooltipState.y}px`,
                transform: "translateY(-50%)"
              }}
            >
              {nameTooltipState.fullName}
            </NameTooltipContainer>
          </ScrollableChartContent>
        )}
      </ChartPlaceholder>
    </ChartContainer>
  );
};

// Vertical chart for medication variants
const VariantsChart = ({
  variantData,
  selectedMedication,
  isLoading = false
}) => {
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    variant: "",
    orders: 0,
    note: ""
  });

  // Add state for the variant name tooltip
  const [nameTooltipState, setNameTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    fullName: ""
  });

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const nameTooltipRef = useRef(null); // New ref for the name tooltip
  const scrollContainerRef = useRef(null);

  // Use the responsive hook to get current dimensions
  const dimensions = useResponsiveChart(containerRef);

  // Filter variants based on selected medication
  const filteredVariants = selectedMedication
    ? variantData.filter((v) => v.medicine_name === selectedMedication.name)
    : [];

  if (!selectedMedication) {
    return (
      <ChartContainer ref={containerRef} className="variants-chart">
        <ChartTitle>Medication Variants</ChartTitle>
        <ChartPlaceholder className="empty">
          <EmptyState>
            <div className="icon">🔍</div>
            <div className="message">
              Select a medication to view its variants
            </div>
          </EmptyState>
        </ChartPlaceholder>
      </ChartContainer>
    );
  }

  if (filteredVariants.length === 0) {
    return (
      <ChartContainer ref={containerRef} className="variants-chart">
        <ChartTitle>Variants for {selectedMedication.name}</ChartTitle>
        <ChartPlaceholder className="empty">
          <EmptyState>
            <div className="icon">📋</div>
            <div className="message">No variants found for this medication</div>
          </EmptyState>
        </ChartPlaceholder>
      </ChartContainer>
    );
  }

  // Sort variants by orders (descending)
  const sortedData = [...filteredVariants].sort((a, b) => b.orders - a.orders);

  // Setup chart dimensions and scales for vertical bars
  const chartWidth = dimensions.width || 600;
  const chartHeight = 400; // Fixed height for vertical chart

  // Simplified margins with more space for vertical centering
  const margin = {
    top: 30,
    right: 30,
    bottom: 80, // Keep space for labels below
    left: 20
  };

  const width = chartWidth - margin.left - margin.right;
  const height = chartHeight - margin.top - margin.bottom;

  // For vertical chart, calculate bar settings
  const idealBarWidth = 60; // Slightly wider bars
  const idealBarPadding = 30; // More space between bars

  // Calculate total width needed for all bars with ideal spacing
  const totalBarCount = sortedData.length;
  const totalIdealWidth =
    totalBarCount * idealBarWidth + (totalBarCount - 1) * idealBarPadding;

  // If we have few bars, center them in the available space
  const useFullWidth = totalIdealWidth > width;

  // Calculate actual bar width and padding based on available space
  const barWidth = useFullWidth
    ? Math.min(
        idealBarWidth,
        width / totalBarCount -
          (idealBarPadding * (totalBarCount - 1)) / totalBarCount
      )
    : idealBarWidth;

  const barPadding = useFullWidth
    ? Math.min(
        idealBarPadding,
        (width - barWidth * totalBarCount) / (totalBarCount - 1 || 1)
      )
    : idealBarPadding;

  // Calculate left margin adjustment to center the bars if not using full width
  const chartStartX = useFullWidth
    ? margin.left
    : margin.left +
      (width - (totalBarCount * barWidth + (totalBarCount - 1) * barPadding)) /
        2;

  // Calculate max orders for scale
  const maxOrders = Math.max(...sortedData.map((d) => d.orders), 1);

  // Calculate the maximum height needed for the tallest bar
  const maxBarHeight = height * 0.8; // Use 80% of available height

  // Calculate the vertical offset to center the bars in the available space
  const verticalOffset = (height - maxBarHeight) / 2 + margin.top;

  // Calculate scales for vertical bars - centered horizontally and vertically
  const xScale = (index) => chartStartX + (barWidth + barPadding) * index;
  const yScale = (value) =>
    height + margin.top - (value / maxOrders) * maxBarHeight - verticalOffset;

  // Handle bar hover for tooltip
  const handleBarHover = (event, d) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set tooltip state with exact mouse position
    setTooltipState({
      visible: true,
      x: mouseX + 10,
      y: mouseY - 30, // Position above the mouse for vertical chart
      variant: d.variant_display,
      orders: d.orders,
      note: d.note || ""
    });
  };

  const handleBarLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // New handler for variant label hover
  const handleVariantLabelHover = (event, variantName) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set name tooltip state
    setNameTooltipState({
      visible: true,
      x: mouseX,
      y: mouseY - 25, // Position above the mouse cursor
      fullName: variantName
    });
  };

  const handleVariantLabelLeave = () => {
    setNameTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // Generate different colors for variant bars
  const getVariantColor = (index) => {
    // Using shades of the primary chart color
    const colors = [
      "#B6CCF3",
      "#C5D8FA",
      "#D3E4F2",
      "#E1F0F9",
      "#F0FBFF",
      "#FFF5FA",
      "#FFE9F5"
    ];

    return colors[index % colors.length];
  };

  // Maximum characters for variant names before truncation
  const MAX_VARIANT_CHARS = 10;

  return (
    <ChartContainer ref={containerRef} className="variants-chart">
      <ChartTitle>{selectedMedication.name}</ChartTitle>

      <ChartPlaceholder ref={scrollContainerRef}>
        {isLoading && (
          <LoadingOverlay>
            <div className="loading-spinner" />
          </LoadingOverlay>
        )}

        <ScrollableChartContent ref={chartRef}>
          <svg
            width="100%"
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Invisible baseline for reference (no visible axes) */}
            <line
              x1={0}
              y1={height + verticalOffset}
              x2={chartWidth}
              y2={height + verticalOffset}
              stroke="transparent"
            />

            {/* Bars with mouse event handlers */}
            {sortedData.map((d, i) => {
              const barColor = getVariantColor(i);
              const xPosition = xScale(i);
              const barHeight = height - (yScale(d.orders) - margin.top);

              // Truncate variant name for display
              const truncatedVariant = truncateText(
                d.variant_display,
                MAX_VARIANT_CHARS
              );

              return (
                <g
                  key={i}
                  onMouseMove={(e) => handleBarHover(e, d)}
                  onMouseLeave={handleBarLeave}
                >
                  {/* Bar */}
                  <rect
                    x={xPosition}
                    y={yScale(d.orders)}
                    width={barWidth}
                    height={barHeight}
                    fill={barColor}
                    rx="6" // Slightly more rounded corners
                    opacity="0.85"
                    className="chart-bar"
                  />

                  {/* Order count above each bar */}
                  <text
                    x={xPosition + barWidth / 2}
                    y={yScale(d.orders) - 10}
                    fontSize="14" // Larger font
                    fontWeight="600"
                    fill="var(--text-primary)"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    {d.orders}
                  </text>

                  {/* Variant display below the bars without rotation */}
                  <text
                    x={xPosition + barWidth / 2}
                    y={height + verticalOffset}
                    fontSize="12"
                    fill="var(--text-secondary)"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    onMouseOver={(e) =>
                      handleVariantLabelHover(e, d.variant_display)
                    }
                    onMouseLeave={handleVariantLabelLeave}
                    style={{ cursor: "default" }}
                  >
                    {truncatedVariant.length > MAX_VARIANT_CHARS
                      ? truncatedVariant.slice(0, MAX_VARIANT_CHARS) + "..."
                      : truncatedVariant}
                  </text>

                  {/* Note indicator if present */}
                  {d.note && (
                    <g
                      transform={`translate(${xPosition + barWidth / 2}, ${
                        yScale(d.orders) - 30
                      })`}
                    >
                      <circle
                        r="8"
                        fill="#f0f0f0"
                        stroke="#cecece"
                        strokeWidth="1"
                      />
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="10"
                        fontWeight="bold"
                        fill="#666666"
                      >
                        i
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* Screen reader description */}
            <desc>
              Vertical bar chart showing variants of {selectedMedication.name}{" "}
              by orders.
              {sortedData
                .map((d) => `${d.variant_display} has ${d.orders} orders.`)
                .join(" ")}
            </desc>
          </svg>

          {/* Tooltip for bar hover */}
          <TooltipContainer
            ref={tooltipRef}
            className={tooltipState.visible ? "visible" : ""}
            style={{
              position: "absolute",
              left: `${tooltipState.x}px`,
              top: `${tooltipState.y}px`
            }}
          >
            <div className="tooltip-title">{tooltipState.variant}</div>
            <div className="tooltip-count">
              <div
                className="tooltip-color"
                style={{
                  backgroundColor: getVariantColor(
                    sortedData.findIndex(
                      (v) => v.variant_display === tooltipState.variant
                    )
                  )
                }}
              />
              <span>{tooltipState.orders} Orders</span>
            </div>
            {tooltipState.note && (
              <div className="tooltip-note">Note: {tooltipState.note}</div>
            )}
          </TooltipContainer>

          {/* Name tooltip for showing full variant name on hover */}
          <NameTooltipContainer
            ref={nameTooltipRef}
            className={nameTooltipState.visible ? "visible" : ""}
            style={{
              position: "absolute",
              left: `${nameTooltipState.x}px`,
              top: `${nameTooltipState.y}px`
            }}
          >
            {nameTooltipState.fullName}
          </NameTooltipContainer>
        </ScrollableChartContent>
      </ChartPlaceholder>
      <PharmacyLabel>
        {selectedMedication.pharmacy_name}
      </PharmacyLabel>
    </ChartContainer>
  );
};

const PharmacySelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    min-width: 160px;
  }

  &:focus {
    outline: none;
    border-color: var(--chart-primary);
    box-shadow: 0 0 0 1px var(--chart-primary-translucent);
  }
`;

// Main component that contains both charts
const MedicationVariantCharts = ({
  medicationData,
  variantData,
  isLoading = false
}) => {
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState("All");

  // Extract unique pharmacy names from medication data
  const pharmacyOptions = React.useMemo(() => {
    if (!medicationData || !medicationData.length) return [];
    const unique = [...new Set(medicationData.map((m) => m.pharmacy_name).filter(Boolean))];
    return unique.sort();
  }, [medicationData]);

  // Filter medication data by selected pharmacy
  const filteredMedicationData = React.useMemo(() => {
    if (!medicationData) return [];
    if (selectedPharmacy === "All") return medicationData;
    return medicationData.filter((m) => m.pharmacy_name === selectedPharmacy);
  }, [medicationData, selectedPharmacy]);

  // Filter variant data to only include variants for visible medications
  const filteredVariantData = React.useMemo(() => {
    if (!variantData) return [];
    if (selectedPharmacy === "All") return variantData;
    const visibleMedNames = new Set(filteredMedicationData.map((m) => m.name));
    return variantData.filter((v) => visibleMedNames.has(v.medicine_name));
  }, [variantData, selectedPharmacy, filteredMedicationData]);

  const handleMedicationSelect = (medication) => {
    setSelectedMedication(medication);
  };

  const handlePharmacyChange = (e) => {
    setSelectedPharmacy(e.target.value);
    setSelectedMedication(null);
  };

  return (
    <ChartGrid className="medication-variant-charts">
      <MedicationsChart
        medicationData={filteredMedicationData}
        onMedicationSelect={handleMedicationSelect}
        selectedMedication={selectedMedication}
        isLoading={isLoading}
        selectedPharmacy={selectedPharmacy}
        pharmacyOptions={pharmacyOptions}
        onPharmacyChange={handlePharmacyChange}
      />
      <VariantsChart
        variantData={filteredVariantData}
        selectedMedication={selectedMedication}
        isLoading={isLoading}
      />
    </ChartGrid>
  );
};

export default MedicationVariantCharts;
